
import React from 'react';
import Layout from '../components/layout/Layout';
import { BRAND } from '../data/mock';
import { Shield } from 'lucide-react';

const PrivacyPolicyPage = () => {
  return (
    <Layout>
      {/* Header */}
      <section className="relative py-20 bg-gradient-to-br from-[hsl(var(--cream))] via-white to-[hsl(var(--peach))]">
        <div className="absolute top-20 left-10 w-72 h-72 bg-[hsl(350,70%,65%)]/10 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[hsl(var(--lavender))] mb-6">
            <Shield className="w-8 h-8 text-[hsl(var(--lavender-dark))]" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-gray-600">Last updated: June 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">

            <div className="bg-[hsl(var(--cream))] rounded-2xl p-6 mb-10 border border-[hsl(350,70%,65%)]/20">
              <p className="text-gray-700 leading-relaxed m-0">
                At <strong>{BRAND.name}</strong>, we are committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, and safeguard your personal 
                information when you use our website and services.
              </p>
            </div>

            {[
              {
                title: "1. Information We Collect",
                content: [
                  "Personal identification information such as your name, email address, phone number, and shipping address when you place an order.",
                  "Photos and images you voluntarily share with us for the purpose of creating custom printed products.",
                  "Payment information processed securely through our payment partners. We do not store your card details on our servers.",
                  "Usage data such as pages visited, time spent on site, and browser type to improve our website experience.",
                  "Communication records including WhatsApp messages and Instagram DMs when you contact us for orders or support."
                ]
              },
              {
                title: "2. How We Use Your Information",
                content: [
                  "To process and fulfill your orders including creating custom artwork and shipping products to you.",
                  "To communicate with you about your order status, artwork previews, and delivery updates.",
                  "To respond to your queries and provide customer support.",
                  "To send you promotional offers and updates only if you have opted in. You can unsubscribe at any time.",
                  "To improve our website, services, and customer experience based on usage patterns.",
                  "To comply with legal obligations and resolve any disputes."
                ]
              },
              {
                title: "3. Photos & Artwork",
                content: [
                  "Photos you share with us are used solely for the purpose of creating your custom print.",
                  "We do not sell, share, or publish your photos without your explicit written consent.",
                  "With your permission, we may use transformed artwork (not original photos) for our portfolio or social media to showcase our work.",
                  "You retain full ownership of your original photos at all times.",
                  "Artwork created by us based on your photos is jointly owned. We may use it for portfolio purposes unless you request otherwise."
                ]
              },
              {
                title: "4. Data Sharing",
                content: [
                  "We do not sell your personal data to third parties under any circumstances.",
                  "We share only necessary information with trusted service providers such as shipping partners and payment gateways to fulfill your order.",
                  "We may disclose information when required by law or to protect the rights and safety of our customers and business.",
                  "Our payment processing is handled by secure third-party gateways compliant with PCI-DSS standards."
                ]
              },
              {
                title: "5. Data Security",
                content: [
                  "We implement industry-standard security measures to protect your personal information.",
                  "Our website uses HTTPS encryption to ensure secure data transmission.",
                  "Access to your personal data is restricted to authorized team members only on a need-to-know basis.",
                  "While we take every precaution, no method of internet transmission is 100% secure. We cannot guarantee absolute security."
                ]
              },
              {
                title: "6. Cookies",
                content: [
                  "Our website uses cookies to enhance your browsing experience and remember your preferences.",
                  "We use analytics cookies to understand how visitors use our website so we can improve it.",
                  "You can control cookie settings through your browser. Disabling cookies may affect some website functionality.",
                  "We do not use cookies to collect personal information without your knowledge."
                ]
              },
              {
                title: "7. Your Rights",
                content: [
                  "You have the right to access the personal information we hold about you at any time.",
                  "You can request correction of any inaccurate or incomplete personal data.",
                  "You can request deletion of your personal data subject to legal and contractual obligations.",
                  "You can opt out of marketing communications at any time by contacting us.",
                  "To exercise any of these rights, please contact us at " + BRAND.email
                ]
              },
              {
                title: "8. Children's Privacy",
                content: [
                  "Our services are not directed to children under the age of 13.",
                  "We do not knowingly collect personal information from children under 13.",
                  "If you believe a child has provided us with personal information, please contact us immediately and we will delete it."
                ]
              },
              {
                title: "9. Changes to This Policy",
                content: [
                  "We may update this Privacy Policy from time to time to reflect changes in our practices or for legal reasons.",
                  "We will notify you of significant changes by posting the new policy on this page with an updated date.",
                  "Your continued use of our services after changes are posted constitutes your acceptance of the updated policy."
                ]
              },
              {
                title: "10. Contact Us",
                content: [
                  "If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:",
                  "Email: " + BRAND.email,
                  "Instagram: @aura_printiqo",
                  "We aim to respond to all privacy-related queries within 48 hours."
                ]
              }
            ].map((section, index) => (
              <div key={index} className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                  {section.title}
                </h2>
                <ul className="space-y-3">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-[hsl(350,70%,65%)] mt-2 flex-shrink-0" />
                      <span className="text-gray-600 leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PrivacyPolicyPage;
